<template>
  <v-dialog
    :value="value"
    max-width="480"
    scrollable
    @input="$emit('input', $event)"
  >
    <v-card color="grey darken-4" dark>
      <v-card-title>
        <v-icon left>mdi-lightbulb-group</v-icon>
        Geräte auf der Karte
        <v-spacer />
        <v-btn icon @click="$emit('input', false)"><v-icon>mdi-close</v-icon></v-btn>
      </v-card-title>
      <v-card-subtitle>
        Lampen, Steckdosen und Routinen-Trigger platzieren, schalten oder von der Karte nehmen.
      </v-card-subtitle>
      <v-card-text class="pa-0">
        <v-list color="transparent" dense>
          <v-list-item
            v-for="device in sortedDevices"
            :key="device.id"
          >
            <v-list-item-icon class="mr-3">
              <v-icon :color="device.on ? 'amber' : 'grey'">{{ icon(device) }}</v-icon>
            </v-list-item-icon>
            <v-list-item-content>
              <v-list-item-title>{{ device.display_name }}</v-list-item-title>
              <v-list-item-subtitle>
                <span v-if="device.category === 'plug' && device.apower != null">{{ device.apower.toFixed(1) }} W · </span>
                <span v-if="device.reachable === false" class="red--text">nicht erreichbar · </span>
                {{ device.map_position ? 'auf der Karte' : 'nicht platziert' }}
              </v-list-item-subtitle>
            </v-list-item-content>
            <v-list-item-action class="flex-row align-center my-0">
              <v-switch
                :input-value="device.on"
                dense
                hide-details
                class="mt-0 mr-1"
                @change="$emit('toggle', device, $event)"
              />
              <v-tooltip top open-delay="300">
                <template #activator="{ on, attrs }">
                  <v-btn v-bind="attrs" icon small v-on="on" @click="$emit('place', device)">
                    <v-icon small>{{ device.map_position ? 'mdi-map-marker-path' : 'mdi-map-marker-plus' }}</v-icon>
                  </v-btn>
                </template>
                <span>{{ device.map_position ? 'Auf der Karte verschieben' : 'Auf der Karte platzieren' }}</span>
              </v-tooltip>
              <v-tooltip top open-delay="300">
                <template #activator="{ on, attrs }">
                  <v-btn
                    v-bind="attrs"
                    icon
                    small
                    :disabled="!device.map_position"
                    v-on="on"
                    @click="$emit('remove', device)"
                  >
                    <v-icon small>mdi-map-marker-remove</v-icon>
                  </v-btn>
                </template>
                <span>Von der Karte entfernen</span>
              </v-tooltip>
            </v-list-item-action>
          </v-list-item>
          <v-list-item v-if="!devices.length">
            <v-list-item-content>
              <div class="text-center grey--text py-2">Keine Geräte geladen.</div>
            </v-list-item-content>
          </v-list-item>

          <template v-if="routines.length">
            <v-divider class="my-1" />
            <v-subheader>Routinen-Trigger</v-subheader>
            <v-list-item
              v-for="routine in sortedRoutines"
              :key="routine.id"
            >
              <v-list-item-icon class="mr-3">
                <v-icon :color="routine.active ? 'deep-purple lighten-2' : 'grey'">mdi-gesture-tap-button</v-icon>
              </v-list-item-icon>
              <v-list-item-content>
                <v-list-item-title>{{ routine.name }}</v-list-item-title>
                <v-list-item-subtitle>
                  {{ routine.on_actions.length }} Aktion(en) ·
                  {{ routine.map_position ? 'auf der Karte' : 'nicht platziert' }}
                </v-list-item-subtitle>
              </v-list-item-content>
              <v-list-item-action class="flex-row align-center my-0">
                <v-switch
                  :input-value="routine.active"
                  dense
                  hide-details
                  class="mt-0 mr-1"
                  @change="$emit('toggle-routine', routine, $event)"
                />
                <v-tooltip top open-delay="300">
                  <template #activator="{ on, attrs }">
                    <v-btn v-bind="attrs" icon small v-on="on" @click="$emit('place-routine', routine)">
                      <v-icon small>{{ routine.map_position ? 'mdi-map-marker-path' : 'mdi-map-marker-plus' }}</v-icon>
                    </v-btn>
                  </template>
                  <span>{{ routine.map_position ? 'Trigger verschieben' : 'Als Trigger auf der Karte platzieren' }}</span>
                </v-tooltip>
                <v-tooltip top open-delay="300">
                  <template #activator="{ on, attrs }">
                    <v-btn
                      v-bind="attrs"
                      icon
                      small
                      :disabled="!routine.map_position"
                      v-on="on"
                      @click="$emit('remove-routine', routine)"
                    >
                      <v-icon small>mdi-map-marker-remove</v-icon>
                    </v-btn>
                  </template>
                  <span>Trigger von der Karte entfernen</span>
                </v-tooltip>
              </v-list-item-action>
            </v-list-item>
          </template>
        </v-list>
      </v-card-text>
    </v-card>
  </v-dialog>
</template>

<script>
import { deviceIcon } from '@/services/devices'

export default {
  name: 'DevicePlaceDialog',
  props: {
    value: { type: Boolean, default: false },
    devices: { type: Array, default: () => [] }, // nur Lampen + Steckdosen
    routines: { type: Array, default: () => [] },
  },
  computed: {
    sortedDevices() {
      // Platzierte zuerst, dann alphabetisch — so steht die Karte im Vordergrund.
      return [...this.devices].sort((a, b) => {
        const pa = a.map_position ? 0 : 1
        const pb = b.map_position ? 0 : 1
        if (pa !== pb) return pa - pb
        return a.display_name.localeCompare(b.display_name, 'de')
      })
    },
    sortedRoutines() {
      return [...this.routines].sort((a, b) => {
        const pa = a.map_position ? 0 : 1
        const pb = b.map_position ? 0 : 1
        if (pa !== pb) return pa - pb
        return a.name.localeCompare(b.name, 'de')
      })
    },
  },
  methods: {
    icon(device) {
      return deviceIcon(device)
    },
  },
}
</script>
